import { useContext, type ButtonHTMLAttributes } from "react";

import { ConfirmDialogContext } from "@/components/ui/confirm-dialog-context";
import { cx } from "@/lib/cx";

/**
 * The four buttons DESIGN.md § Components names — `button-primary/secondary/
 * ghost/destructive` — all 44px tall, no compact desktop variant, ever.
 *
 * Read-only rendering is by absence, not `disabled` (DESIGN.md § Do's and
 * Don'ts): a screen that shouldn't show a control simply doesn't render this
 * component, rather than rendering it with `disabled`. The `disabled` prop
 * this component still accepts is for the unrelated, ordinary case DESIGN.md
 * also describes — "pressing a primary button disables it until the write
 * returns" — which no screen in this story triggers yet.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

/**
 * `active:scale-[0.98]`: press feedback (animation-opportunity sweep,
 * 2026-08-22) — every button in the app is clicked often enough that
 * anything more than a sub-perceptual nudge would read as sluggish, not
 * polished, so this stays fast and small on purpose. `transition-[...]`
 * spells out color and transform explicitly rather than a broader
 * `transition`/`transition-colors` + a second transform utility: two
 * separate transition utilities would both set the same
 * `transition-property` declaration and the later one would silently win
 * outright rather than combine, dropping the other's properties from
 * transitioning at all.
 */
const BASE =
  "inline-flex h-tap-target-min items-center justify-center gap-2 rounded-md text-button font-medium transition-[background-color,border-color,color,scale] duration-150 ease-out active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-accent px-5 text-on-accent hover:bg-accent-hover",
  secondary: "border border-border-strong bg-canvas px-5 text-ink hover:bg-surface-hover",
  ghost: "bg-transparent px-3 text-ink-secondary hover:bg-surface-hover",
  destructive: "bg-danger px-5 text-on-danger hover:bg-danger-hover",
};

/**
 * The runtime half of "the destructive variant appears only inside
 * ConfirmDialog" (DESIGN.md; VC-2.6-09) — a source-text scan
 * (`destructive-button-guard.test.ts`) catches a literal
 * `variant="destructive"` written elsewhere, but cannot catch a value
 * computed at render time (a spread prop, a ternary, a variable). This
 * checks the actual value `Button` is asked to render with, at the actual
 * render site, against `ConfirmDialogContext` — which only `ConfirmDialog`
 * ever sets to `true` — so every way of *computing* "destructive" is
 * caught alike. Thrown, not warned: a destructive button rendered outside
 * a confirm dialog is a defect no screen should ever actually show a real
 * account holder, so surfacing it as a rendering exception (visible in any
 * test or manual pass that exercises the offending code, and in the
 * browser console in production) is stronger than a warning that a busy
 * screen can scroll past.
 */
function assertDestructiveVariantIsAllowedHere(variant: ButtonVariant, insideConfirmDialog: boolean): void {
  if (variant === "destructive" && !insideConfirmDialog) {
    throw new Error(
      'Button variant="destructive" may only render inside ConfirmDialog (DESIGN.md: "appears only ' +
        "inside a confirmation dialog\"). A destructive action in a table row or record header must be a " +
        "ghost button that opens its own ConfirmDialog — the red button is the second step, never the first.",
    );
  }
}

export function Button({ variant = "primary", className, ...props }: ButtonProps) {
  const insideConfirmDialog = useContext(ConfirmDialogContext);
  assertDestructiveVariantIsAllowedHere(variant, insideConfirmDialog);
  return <button type="button" className={cx(BASE, VARIANT_CLASSES[variant], className)} {...props} />;
}

/**
 * The same visual classes `Button` itself renders with, for the one
 * legitimate non-`<button>` case this system has: a real, navigable
 * `<Link>` that needs to *look* like a button (Story 3.9's alert-row
 * record-link action — an actual destination, not a click handler, so it
 * must be a real anchor for keyboard/right-click/"open in new tab" to work,
 * exactly `RelatedRecords.tsx`'s own reasoning for using `<Link>` over a
 * `<tr onClick>`). Never used to build a second `<button>` — that should
 * always just be `Button` itself. `variant` excludes `"destructive"`:
 * `assertDestructiveVariantIsAllowedHere`'s own guard only ever runs inside
 * `Button`'s render, so a destructive-styled link would silently bypass it.
 */
export function buttonClassName(
  variant: Exclude<ButtonVariant, "destructive"> = "ghost",
  className?: string,
): string {
  return cx(BASE, VARIANT_CLASSES[variant], className);
}
